import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { useLingui } from "@lingui/react/macro";
import "./styles/globals.css";
import { authClient } from "./lib/AuthClient";
import { readAuthBootstrap, saveAuthBootstrap, clearAuthBootstrap } from "./lib/AuthBootstrap";
import { identifyUser, resetAnalytics } from "./lib/Analytics";
import { SplashDismissOnMount } from "./splash/SplashDismissOnMount";
import { getDesktopBridge } from "./lib/Desktop";
import { consumeHandoffDeepLink } from "./lib/DesktopAuth";
import { useNotificationNudge } from "./hooks/UseNotificationNudge";
import { useTimezoneNudge } from "./hooks/UseTimezoneNudge";
import { getAppRouter } from "./lib/RouterHandle";
import { withChunkRetry } from "./lib/ChunkRetry";
import { useSyncLocaleFromServer, useSyncThemeFromServer } from "./queries/user-preferences/Query";
import {
  AUTH_FORGOT_PATH,
  AUTH_INVITE_PATH,
  AUTH_LINK_PATH,
  AUTH_LOGIN_PATH,
  AUTH_REGISTER_PATH,
  AUTH_RESET_PATH,
  AUTH_VERIFY_PATH,
  PENDING_INVITATION_KEY,
  PUBLIC_TASK_PATH_PREFIX,
  RATE_LIMITED_PATH,
  navigate,
  usePathname,
} from "./lib/Pathname";
import { useOnboardingStatusQuery } from "./queries/Onboarding";
// LoginPage(비로그인 기본 화면)와 RateLimitedPage(429 최상단 가드)만 정적으로
// 남긴다 — 콜드 진입에서 즉시 떠야 해 lazy 로 미루면 안 되는 두 화면이다.
import { LoginPage } from "./pages/login/Page";
import { RateLimitedPage } from "./pages/rate-limited/Page";

// router.tsx(@tanstack/react-router + 대시보드 라우트 전체)는 로그인 화면에서는
// 필요 없다 — 세션 확립 후에만 렌더되는 이 컴포넌트를 lazy 청크로 분리해 로그인
// 화면의 초기 번들에서 뺀다. 실제 컴포넌트는 router-provider.tsx 참고.
const AppRouterProvider = lazy(withChunkRetry(() => import("./RouterProvider")));

// realtime(agents/react + WebSocket)은 로그인 화면에선 필요 없다 — 세션 확립(또는
// 낙관 부팅 힌트) 후에만 마운트되므로 lazy 청크로 분리해 entry(초기) 번들에서
// 뺀다(agents SDK + local-notifications). 실제 컴포넌트는 components/RealtimeConnection.tsx.
const RealtimeConnection = lazy(withChunkRetry(() => import("./components/RealtimeConnection")));

// pre-router 페이지들도 한 번에 pathname 에 맞는 하나만 렌더되는데, 정적 import 하면
// 9개가 전부 entry(초기) 청크에 박힌다. 로그인/대시보드 콜드 경로에 필요 없는 화면들을
// lazy 청크로 분리한다 — 특히 PublicTaskPage 는 @app/ui 의 무거운 PublicTaskView(+대시보드
// 와 공유하는 UI 다발)를 끌어와 entry 를 가장 많이 부풀리는 주범이다. named export 라
// lazy 가 기대하는 { default } 모양으로 맞춘다.
const PublicTaskPage = lazy(withChunkRetry(() => import("./pages/public-task/Page").then((m) => ({ default: m.PublicTaskPage }))));
const OnboardingPage = lazy(withChunkRetry(() => import("./pages/onboarding/Page").then((m) => ({ default: m.OnboardingPage }))));
const InviteAcceptPage = lazy(withChunkRetry(() => import("./pages/invite/Page").then((m) => ({ default: m.InviteAcceptPage }))));
const VerifyEmailPage = lazy(withChunkRetry(() => import("./pages/verify-email/Page").then((m) => ({ default: m.VerifyEmailPage }))));
const ForgotPasswordPage = lazy(withChunkRetry(() => import("./pages/forgot-password/Page").then((m) => ({ default: m.ForgotPasswordPage }))));
const ResetPasswordPage = lazy(withChunkRetry(() => import("./pages/reset-password/Page").then((m) => ({ default: m.ResetPasswordPage }))));
const DesktopHandoffPage = lazy(withChunkRetry(() => import("./pages/desktop-handoff/Page").then((m) => ({ default: m.DesktopHandoffPage }))));

// lazy 페이지는 청크 로드 전까지 로고 스플래시(#app-splash 오버레이)가 계속 덮는다 —
// fallback 은 빈 화면(null)이지만 그 위를 스플래시가 가리므로 사용자에겐 로고 로더만
// 보인다. 청크가 해소되면 실제 node 와 함께 SplashDismissOnMount 가 마운트되며 스플래시를
// 걷는다. 마커는 fallback 이 아니라 Suspense 경계 "안쪽"에 둬야 한다 — node 가 suspend
// 되는 동안엔 형제인 마커도 마운트되지 않아 스플래시가 유지되고, 해소돼야 함께 마운트된다.
function lazyPage(node: ReactNode) {
  return (
    <Suspense fallback={null}>
      <SplashDismissOnMount />
      {node}
    </Suspense>
  );
}

// 세션이 없어도 로그인으로 튕기지 않는 경로들(App 하단 리다이렉트 게이트 기준).
// 비밀번호 찾기/재설정/이메일 인증 안내는 로그아웃 상태에서도 접근 가능해야 한다.
// /invite 도 마찬가지 — 초대 메일은 받는 사람이 로그인 전일 수 있다.
const AUTH_PATHS = new Set([
  AUTH_LOGIN_PATH,
  AUTH_REGISTER_PATH,
  AUTH_LINK_PATH,
  AUTH_FORGOT_PATH,
  AUTH_RESET_PATH,
  AUTH_VERIFY_PATH,
  AUTH_INVITE_PATH,
]);

// DesktopNotificationNavigation/NativeNotificationNavigation(router.navigate로 알림
// 클릭 이동을 처리하던 컴포넌트)은 router-provider.tsx 로 옮겼다 — 어차피 App의 return
// 트리에서 세션 확립·온보딩 통과 후에만 렌더되던 것들이라, router 청크와 함께 lazy
// 로드돼도 동작이 같다(주석은 그 파일에 그대로 옮겨 뒀다).

export function App() {
  const { t, i18n } = useLingui();
  const { data: session, isPending: isSessionPending } =
    authClient.useSession();
  const pathname = usePathname();
  // 데스크톱/네이티브 앱이 연 "시스템 브라우저 탭"에서의 로그인 핸드오프 경로. 그
  // 브라우저는 데스크톱 브리지도 Capacitor 전역도 없는 일반 웹이라 !getDesktopBridge().
  const isAuthHandoff = !getDesktopBridge() && pathname === AUTH_LINK_PATH;
  // 네이티브 소셜 로그인은 세션 없이 /auth/link?social=... 로 진입해 이 브라우저에서
  // OAuth 를 개시한다 — 세션이 없어도 이 파라미터가 있으면 핸드오프 페이지를 렌더해야
  // 한다(아래 게이트에서 "세션 없으면 로그인 화면"보다 우선). search 는 usePathname 의
  // 구독 대상이 아니지만, 핸드오프는 전체 페이지 네비게이션을 거치므로 렌더 시 1회 읽기로 충분하다.
  const handoffSocialParam = isAuthHandoff
    ? new URLSearchParams(window.location.search).get("social")
    : null;

  // 콜드 스타트 낙관 부팅 힌트 — 마운트 시점 1회만 읽는다(세션/온보딩 왕복이
  // 끝나기 전에도 "최근에 로그인 상태였다"는 근거로 RouterProvider를 먼저
  // 마운트해 순백 화면을 없앤다. 실제 인가는 항상 서버 세션 쿠키가 한다).
  const [bootstrapHint] = useState(() => readAuthBootstrap());

  // bootstrapHint가 "이 세션 유저"의 온보딩 완료를 이미 확인해줬는지 (계정 전환 등으로
  // 힌트가 다른 유저 것일 수 있어 userId 일치까지 확인한다 — session은 서버가 확정한
  // 값이라 이 시점 비교는 안전하다). 온보딩 완료는 서버에서 되돌리는 라우트가 없어
  // 한 번 true면 영구히 true이므로, 확인된 뒤에는 다시 물어볼 필요가 없다.
  const isOnboardingKnownDoneForSession =
    !!session && !!bootstrapHint && bootstrapHint.userId === session.session.userId && bootstrapHint.onboardingCompleted;

  const { data: onboardingStatus, isLoading: isOnboardingLoading } = useOnboardingStatusQuery(
    !!session && !isOnboardingKnownDoneForSession,
  );
  const needsOnboarding = !!session && !isOnboardingKnownDoneForSession && !onboardingStatus?.completed;

  // 세션/온보딩 판정이 확정될 때마다 힌트를 최신 상태로 맞춘다. 로그아웃/세션
  // 무효 확인 시에는 지워야 다음 콜드 스타트가 잘못된 계정으로 낙관 렌더하지 않는다.
  useEffect(() => {
    // 세션/온보딩 판정이 아직 진행 중인지
    const isAuthStateStillSettling = isSessionPending || isOnboardingLoading;
    if (isAuthStateStillSettling) return;
    // 세션이 있고 온보딩 상태 응답도 도착했는지
    const hasConfirmedSessionAndOnboarding = !!session && !!onboardingStatus;
    // 세션이 없는 것으로 확인됐는지
    const isConfirmedNoSession = !session;
    if (hasConfirmedSessionAndOnboarding) {
      saveAuthBootstrap({
        userId: session.session.userId,
        onboardingCompleted: !!onboardingStatus.completed,
      });
      // 여기부터의 이벤트를 이 유저에게 귀속시킨다. identify 는 멱등이라 세션이
      // 유지되는 동안 effect 가 다시 돌아도 안전하다(lib/Analytics.ts).
      identifyUser(session.session.userId);
    } else if (isConfirmedNoSession) {
      clearAuthBootstrap();
      // 로그아웃/세션 만료가 확정된 시점 — 다음 사용자가 이전 계정으로 귀속되지
      // 않도록 익명 ID를 새로 발급한다.
      resetAnalytics();
    }
  }, [session, isSessionPending, onboardingStatus, isOnboardingLoading]);

  // 세션 확립 후 서버에 저장된 테마/언어 선호값과 로컬을 수렴시킨다(기기 간 동기화).
  useSyncThemeFromServer(!!session);
  useSyncLocaleFromServer(!!session);

  // Capacitor/Electron 네이티브 통합(리마인더 액션 리스너, FCM 트레이/알림 내비게이션,
  // 상태바 동기화, 안드로이드 뒤로가기)은 components/PlatformBridge.tsx 로 옮겼다 —
  // main.tsx 가 detectPlatform() !== "web" 일 때만 lazy 마운트하므로 웹 entry(초기)
  // 청크에서 @capacitor/* 와 리마인더에 딸리는 tasks 쿼리(dexie)가 빠진다.

  // Electron 셸 안에서 실행 중이면(window.desktop) withtask:// 딥링크로 전달되는
  // 로그인 핸드오프 토큰을 받아 세션을 확립한다. pages/login의 "브라우저에서
  // 로그인" 버튼 → pages/desktop-handoff가 짝을 이룬다. 받은 뒤 처리(검증·리로드)는
  // 네이티브(PlatformBridge)와 공유하는 consumeHandoffDeepLink 한 곳에 둔다 — 딥링크를
  // "받는 통로"만 셸마다 다르다(여기: Electron preload IPC, 네이티브: capacitor 이벤트).
  useEffect(() => {
    const desktop = getDesktopBridge();
    // 데스크톱 브리지가 없는지
    const isDesktopBridgeAbsent = !desktop;
    if (isDesktopBridgeAbsent) return;
    return desktop.onDeepLink((url) => {
      void consumeHandoffDeepLink(url);
    });
  }, []);

  // Cmd+←/→로 브라우저 뒤로/앞으로가기, Cmd+,로 설정 페이지 이동. 창(웹뷰)에
  // 포커스가 있을 때만 반응하는 window 레벨 keydown이라 Electron/브라우저 모두
  // 동일하게 동작한다. 텍스트 입력 중인 Cmd+←/→(줄 시작/끝 이동)는 건드리지 않도록
  // 화살표는 편집 가능 요소를 건너뛴다. Cmd+,는 맥 표준(환경설정)대로 편집 중에도 동작.
  useEffect(() => {
    function isEditableTarget(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      // input/textarea/contentEditable 인지
      return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;
    }
    function onKeyDown(event: KeyboardEvent) {
      // Cmd/Ctrl+Shift+F/C/A 로 검색/캘린더/전체 화면 이동(Mac 은 Cmd, 그 외는 Ctrl).
      // Shift 조합이라 event.key 가 대문자로 오므로 소문자로 정규화한다. 입력창
      // 포커스 중에도 바로 해당 화면으로 튀도록 아래 편집 가드보다 앞에 둔다.
      // Cmd+Shift+F 를 쓰므로 브라우저 네이티브 Cmd+F(페이지 내 찾기)와는 겹치지 않는다.
      if ((event.metaKey || event.ctrlKey) && event.shiftKey) {
        const router = getAppRouter();
        switch (event.key.toLowerCase()) {
          case "f":
            event.preventDefault();
            void router?.navigate({ to: "/search" });
            return;
          case "c":
            event.preventDefault();
            void router?.navigate({ to: "/calendar" });
            return;
          case "a":
            event.preventDefault();
            void router?.navigate({ to: "/all-tasks" });
            return;
        }
      }
      // Cmd+L → 컴포저 제목 input 에 포커스
      if ((event.metaKey || event.ctrlKey) && !event.shiftKey && event.key.toLowerCase() === "l") {
        const input = document.querySelector<HTMLInputElement>("[data-composer-title]");
        if (input) {
          event.preventDefault();
          input.focus();
          return;
        }
      }
      // Cmd(메타키)가 눌리지 않았으면 무시
      if (!event.metaKey) return;
      if (event.key === ",") {
        event.preventDefault();
        // 라우터 청크가 아직 로드 전(로그인 화면 등)이면 이동할 설정 페이지 자체가
        // 없으므로 no-op — router-handle.ts 참고.
        void getAppRouter()?.navigate({ to: "/settings" });
        return;
      }
      // 화살표는 텍스트 편집 중에는 줄 시작/끝 이동이라 건드리지 않는다
      if (isEditableTarget(event.target)) return;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        window.history.back();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        window.history.forward();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // 로그인(세션 확립) 후 알림 권한을 pre-permission priming 으로 다룬다: 이미
  // 허용됐으면 조용히 구독을 갱신하고, 아직 안 물어봤으면 콜드 팝업 대신 "알림 켜기"
  // 소프트 토스트를 1회 띄운다(hooks/UseNotificationNudge.ts). 거부/미지원은
  // 설정 화면의 권한 섹션에서만 안내한다.
  useNotificationNudge(Boolean(session));

  // 로그인 후, 브라우저가 감지한 현재 위치의 타임존이 저장된 user.timezone 과 다르면
  // "바꾸시겠어요?" 소프트 토스트를 띄운다(hooks/UseTimezoneNudge.ts). 무시한 타임존은
  // 기억해 같은 위치에서는 다시 띄우지 않는다.
  useTimezoneNudge(Boolean(session), session?.user.timezone);

  // Electron 셸이면 main process에 현재 userId를 알려 유저별 room에 연결하게 한다
  // (웹 푸시가 Electron에서 신뢰할 수 없어 이 실시간 채널이 데스크톱의 실제 알림
  // 전달 경로다 — apps/desktop/src/main/realtime.ts 참고). 로그아웃 시 null로 해제.
  useEffect(() => {
    getDesktopBridge()?.setUserId(session?.session.userId ?? null);
  }, [session]);

  // Electron main process 에는 lingui 가 없고 리마인더 알림이 렌더러를 우회해 들어오므로,
  // 알림 버튼(완료/스누즈) 라벨을 현재 로케일로 번역해 main 에 미리 넘겨둔다. 로케일이
  // 바뀔 때마다 다시 push 한다(apps/desktop/src/main/NotificationManager.ts 참고).
  useEffect(() => {
    getDesktopBridge()?.setReminderLabels({ complete: t`완료`, snooze: t`10분 뒤 다시` });
  }, [t, i18n.locale]);

  // 알림 클릭 시 서비스워커가 postMessage로 전달하는 url로 이동한다(이미 창이
  // 열려있는 경우 sw.js의 notificationclick 핸들러 참고).
  useEffect(() => {
    // 서비스워커를 지원하지 않는지
    const isServiceWorkerUnsupported = !("serviceWorker" in navigator);
    if (isServiceWorkerUnsupported) return;
    const onMessage = (event: MessageEvent) => {
      // 알림 클릭 타입 메시지인지
      const isNotificationClickType = event.data?.type === "notification-click";
      // 알림 클릭 메시지이고 이동할 url 이 문자열인지 (data 접근은 타입 확인 뒤에만 — 단락 평가 보존)
      const isNotificationClickMessage =
        isNotificationClickType && typeof event.data.url === "string";
      if (isNotificationClickMessage) {
        // 라우터 청크가 아직 로드 전이면(=로그인 화면에 postMessage 도착) 이동할 대시보드
        // 자체가 없으므로 no-op — router-handle.ts 참고.
        void getAppRouter()?.navigate({ href: event.data.url });
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, []);

  // Electron 네이티브 알림 클릭 → task 이동은 router-provider.tsx 의
  // <DesktopNotificationNavigation/>가 담당한다. 여기(App 본문)에 두면 세션 판정 중
  // (App이 null을 반환해 라우터 청크가 아직 로드 전인 로딩 단계)에 이펙트가 먼저
  // 실행돼, 콜드 오픈으로 보관된 url을 라우터가 마운트되기 전에 소진해버려 이동이
  // 유실된다 — 그래서 라우팅이 살아있는 트리 안에서만 mount 되는 컴포넌트로 분리한다.

  useEffect(() => {
    // rate limit(429) 대기 화면도 이 게이트에서 제외한다 — 여기 있는 동안 세션이
    // 없다고(또는 429 로) 로그인으로 튕기면 대기·복귀 흐름이 깨진다. 복귀는 대기
    // 화면이 저장해 둔 경로로 직접 navigate 한다(pages/rate-limited/Page.tsx).
    if (pathname === RATE_LIMITED_PATH) return;
    // 공개 공유 링크는 비로그인 열람이 목적 — 세션이 없어도 로그인으로 튕기면 안 되므로
    // 이 리다이렉트 게이트에서 아예 제외한다(early return 은 렌더만 막고 effect 는 돈다).
    if (pathname.startsWith(PUBLIC_TASK_PATH_PREFIX)) return;
    // 세션이 있는데 온보딩 상태를 아직 로딩 중인지
    const isOnboardingStatusLoading = session && isOnboardingLoading;
    // 세션/온보딩 판정이 아직 진행 중인지
    const isAuthStateSettling = isSessionPending || isOnboardingStatusLoading;
    if (isAuthStateSettling) return;
    // 온보딩 경로가 아닌지
    const isNotOnboardingPath = pathname !== "/onboarding";
    // 온보딩 리다이렉트가 필요한지
    const needsOnboardingRedirect = session && needsOnboarding && isNotOnboardingPath;
    // 온보딩이 필요 없는지
    const isOnboardingComplete = !needsOnboarding;
    // 온보딩을 마친 세션인지
    const isOnboardedSession = session && isOnboardingComplete;
    // 세션이 없는지
    const hasNoSession = !session;
    // 인증 경로가 아닌지
    const isNotAuthPath = !AUTH_PATHS.has(pathname);
    // 로그인 화면으로 보내야 하는지
    const needsLoginRedirect = hasNoSession && isNotAuthPath;
    if (needsOnboardingRedirect) {
      navigate("/onboarding");
    } else if (isOnboardedSession) {
      // 로그인/가입 전에 초대 수락 화면(pages/invite)이 남겨둔, 되돌아가야 할 초대 id.
      // 로그인 흐름(비밀번호 로그인은 이 effect가, 가입은 verify-email의 전체 리로드가
      // 세션을 확립한 뒤) 어디로 들어오든 여기서 한 번에 가로채 /invite 로 돌려보낸다.
      const pendingInvitationId = window.localStorage.getItem(PENDING_INVITATION_KEY);
      // 이미 초대 화면에 있는지
      const isInvitePath = pathname === AUTH_INVITE_PATH;
      // 되돌아갈 대기 중인 초대가 있는지
      const needsInviteReturn = !!pendingInvitationId && !isInvitePath;
      // 온보딩 경로인지
      const isOnboardingPath = pathname === "/onboarding";
      // 대시보드 경로인지
      const isDashboardPath = pathname.startsWith("/dashboard");
      // 로그인 경로인지
      const isLoginPath = pathname === AUTH_LOGIN_PATH;
      // 회원가입 경로인지
      const isRegisterPath = pathname === AUTH_REGISTER_PATH;
      // 인증 진입 경로인지
      const isAuthEntryPath = isLoginPath || isRegisterPath;
      if (needsInviteReturn) {
        navigate(`${AUTH_INVITE_PATH}?id=${pendingInvitationId}`);
      } else if (isOnboardingPath) {
        navigate("/");
      } else if (isDashboardPath) {
        navigate(pathname === "/dashboard" ? "/" : pathname.slice("/dashboard".length));
      } else if (isAuthEntryPath) {
        navigate("/");
      }
    } else if (needsLoginRedirect) {
      navigate(AUTH_LOGIN_PATH);
    }
  }, [
    session,
    isSessionPending,
    needsOnboarding,
    isOnboardingLoading,
    pathname,
  ]);

  // rate limit(429) 대기 화면 — 세션 판정(429 로 null 이 되어 로그인으로 튕기는 것)보다
  // 먼저 가로채야 하므로 모든 세션 분기보다 앞에 둔다. 이 화면은 백엔드 요청을 전혀
  // 하지 않아 세션/온보딩 로딩과도 무관하다(pages/rate-limited/Page.tsx).
  const isRateLimitedPath = pathname === RATE_LIMITED_PATH;
  if (isRateLimitedPath) {
    return (
      <>
        <SplashDismissOnMount />
        <RateLimitedPage />
      </>
    );
  }

  // task 공개 공유 링크 — 로그인 여부·세션 판정과 무관하게 즉시 렌더한다(대시보드
  // 라우터/셸이나 로더를 띄우지 않는다). /invite·/verify-email 이 세션 분기보다 먼저
  // 처리되는 것과 같은 패턴이고, 세션 왕복이 끝나기 전에도 열려야 하므로 그보다도 앞에 둔다.
  const isPublicTaskPath = pathname.startsWith(PUBLIC_TASK_PATH_PREFIX);
  if (isPublicTaskPath) {
    // `/public/tasks/<token>` 또는 `/public/tasks/<token>/<taskId>`(자손 subtask 드릴인).
    // 토큰은 앵커 자격증명, taskId 는 그 서브트리 안 대상 노드 선택자(서버가 소속 검증).
    const rest = pathname.slice(PUBLIC_TASK_PATH_PREFIX.length).replace(/\/+$/, "");
    const [token, taskId] = rest.split("/");
    return lazyPage(<PublicTaskPage token={token} taskId={taskId} />);
  }

  // 세션이 있는데 온보딩 상태를 아직 로딩 중인지
  const isOnboardingStatusLoading = session && isOnboardingLoading;
  // 세션/온보딩 판정이 아직 진행 중인지
  const isAuthStateSettling = isSessionPending || isOnboardingStatusLoading;
  if (isAuthStateSettling) {
    // 낙관 부팅 힌트가 "온보딩까지 마친 로그인 상태"였는지
    const isOptimisticDashboardHint = !!bootstrapHint?.onboardingCompleted;
    // 낙관 부팅 힌트가 "온보딩까지 마친 로그인 상태"였다면, 서버 확정을 기다리지
    // 않고 대시보드 셸을 먼저 그린다 — 힌트가 틀렸다면(로그아웃/세션 만료) 위
    // 리다이렉트 effect가 판정 확정 즉시 로그인 화면으로 되돌린다.
    if (isOptimisticDashboardHint) {
      return (
        <>
          {!getDesktopBridge() && (
            <Suspense fallback={null}>
              <RealtimeConnection userId={bootstrapHint.userId} />
            </Suspense>
          )}
          <Suspense fallback={null}>
            <SplashDismissOnMount />
            <AppRouterProvider />
          </Suspense>
        </>
      );
    }
    // 힌트는 있는데 온보딩 미완료였던 경우 — 온보딩 화면을 먼저 보여준다.
    if (bootstrapHint) {
      return lazyPage(<OnboardingPage />);
    }
    // 힌트가 아예 없는 진짜 첫 방문/장기 미접속 — 보여줄 캐시가 없다. 로고 스플래시
    // (#app-splash 오버레이)가 아직 덮고 있으므로 여기선 아무것도 그리지 않고, 세션
    // 판정이 끝나 실제 화면(로그인/대시보드 등)이 마운트될 때 그쪽 SplashDismissOnMount
    // 가 스플래시를 걷는다. 판정이 영영 안 끝나는 극단은 SplashController 의 안전
    // 데드라인이 받쳐 준다.
    return null;
  }

  // 이메일 인증 코드 입력 경로인지 — 인증을 마쳐야 로그인되므로(requireEmailVerification=true)
  // 가입 직후에는 세션이 없다. 아래 "세션 없으면 로그인 화면" 분기에 먹히지 않도록 먼저 처리한다.
  const isVerifyEmailPath = pathname === AUTH_VERIFY_PATH;
  if (isVerifyEmailPath) {
    return lazyPage(<VerifyEmailPage />);
  }

  // 팀 초대 수락 경로. 로그인 여부와 무관하게 열려야 해서(초대 메일을 받은 사람이
  // 세션이 없을 수 있다) 세션 분기보다 먼저 처리한다 — 화면 내부에서 세션 유무에
  // 따라 "로그인/가입 안내"와 "수락 버튼"을 나눠 보여준다(pages/invite/Page.tsx).
  const isInvitePath = pathname === AUTH_INVITE_PATH;
  if (isInvitePath) {
    return lazyPage(<InviteAcceptPage />);
  }

  // 데스크톱/네이티브 로그인 핸드오프. "핸드오프"란 웹뷰 안에서 직접 OAuth 를 못 하니
  // (임베디드 웹뷰는 state 쿠키 유실·구글 차단) 진짜 시스템 브라우저에서 로그인을 마친 뒤,
  // 그 세션을 1회용 토큰 딥링크로 앱 셸에 "넘겨주는(hand off)" 것을 말한다(자세한 배경은
  // lib/NativeAuth.ts). 이 경로(/auth/link)는 그 브라우저 탭에서 열린다: 세션이 있으면
  // (데스크톱: 이미 로그인된 브라우저, 네이티브: OAuth 완주 후 복귀) 토큰을 발급하고,
  // 세션이 없어도 social 파라미터가 있으면(네이티브가 이 브라우저에서 OAuth 를 개시)
  // 핸드오프 페이지가 그 흐름을 처리한다 — 그래서 아래 "세션 없으면 로그인 화면" 분기보다
  // 앞에 둔다(pages/desktop-handoff).
  if (isAuthHandoff && (session || handoffSocialParam)) {
    return lazyPage(<DesktopHandoffPage />);
  }

  // 세션이 없는지
  const hasNoSession = !session;
  if (hasNoSession) {
    // 비밀번호 찾기 경로인지
    const isForgotPasswordPath = pathname === AUTH_FORGOT_PATH;
    if (isForgotPasswordPath) {
      return lazyPage(<ForgotPasswordPage />);
    }
    // 비밀번호 재설정 경로인지(메일 링크가 ?token=... 을 붙여 보내는 경로)
    const isResetPasswordPath = pathname === AUTH_RESET_PATH;
    if (isResetPasswordPath) {
      return lazyPage(<ResetPasswordPage />);
    }
    return (
      <>
        <SplashDismissOnMount />
        <LoginPage />
      </>
    );
  }

  if (needsOnboarding) {
    return lazyPage(<OnboardingPage />);
  }

  return (
    <>
      {!getDesktopBridge() && (
        <Suspense fallback={null}>
          <RealtimeConnection userId={session.session.userId} />
        </Suspense>
      )}
      <Suspense fallback={null}>
        <SplashDismissOnMount />
        <AppRouterProvider />
      </Suspense>
    </>
  );
}
